export const STORAGE_KEY = 'aurelia.display_currency.v1';
export const CURRENCY_DECIMALS = Object.freeze({ SGD: 2, USD: 2, EUR: 2, GBP: 2, PHP: 2, THB: 2, MYR: 2, IDR: 2, VND: 0, CNY: 2 });
export const COUNTRY_CURRENCY = Object.freeze({
  SG: 'SGD', PH: 'PHP', TH: 'THB', US: 'USD', GB: 'GBP', MY: 'MYR', ID: 'IDR', VN: 'VND', CN: 'CNY',
  AT: 'EUR', BE: 'EUR', CY: 'EUR', DE: 'EUR', EE: 'EUR', ES: 'EUR', FI: 'EUR', FR: 'EUR', GR: 'EUR',
  HR: 'EUR', IE: 'EUR', IT: 'EUR', LT: 'EUR', LU: 'EUR', LV: 'EUR', MT: 'EUR', NL: 'EUR', PT: 'EUR', SI: 'EUR', SK: 'EUR'
});
export const DEFAULT_STRINGS = Object.freeze({
  estimate: 'Estimated {amount} · source {source}',
  sourcePrice: 'Source {source}',
  statusDetecting: 'Showing SGD source prices while checking your region. Draft checkout currency: {checkoutCurrency}.',
  statusDetected: 'Reference currency {currency} selected for {country}. Draft checkout currency: {checkoutCurrency}.',
  statusManual: 'Reference currency {currency} selected. Draft checkout currency: {checkoutCurrency}.',
  statusFallback: 'Showing SGD source prices. Choose a reference currency. Draft checkout currency: {checkoutCurrency}.',
  statusUnavailable: 'Reference estimates are unavailable. Draft checkout currency: {checkoutCurrency}.',
  rateDate: 'Reference rates dated {date}.',
  note: 'Per-item reference only. Draft checkout currency: {checkoutCurrency}. Bank or card conversion may differ.'
});

const PRICE_SELECTOR = '[data-aurelia-reference-price]';
const NOTE_SELECTOR = '[data-aurelia-reference-note]';
const CHOICE_SELECTOR = 'select[data-currency-choice]';
const own = (object, key) => Object.prototype.hasOwnProperty.call(object, key);
const supported = (currency) => typeof currency === 'string' && own(CURRENCY_DECIMALS, currency);

export function positiveDecimal(value) {
  if (typeof value !== 'string' || !/^(?:0|[1-9]\d{0,29})(?:\.\d{1,18})?$/.test(value)) return null;
  const [whole, fraction = ''] = value.split('.');
  const numerator = BigInt(whole + fraction);
  return numerator > 0n ? { numerator, denominator: 10n ** BigInt(fraction.length) } : null;
}

export function convertPrice(sourcePrice, currency, rates) {
  const price = positiveDecimal(sourcePrice);
  if (!price || !supported(currency)) return null;
  const rate = currency === 'SGD' ? { numerator: '1', denominator: '1' } : rates?.[currency];
  const top = positiveDecimal(rate?.numerator);
  const bottom = positiveDecimal(rate?.denominator);
  if (!top || !bottom) return null;
  const places = CURRENCY_DECIMALS[currency];
  const numerator = price.numerator * top.numerator * bottom.denominator * (10n ** BigInt(places));
  const denominator = price.denominator * top.denominator * bottom.numerator;
  const rounded = (2n * numerator + denominator) / (2n * denominator);
  if (!places) return rounded.toString();
  const digits = rounded.toString().padStart(places + 1, '0');
  return `${digits.slice(0, -places)}.${digits.slice(-places)}`;
}

export function formatAmount(amount, currency, locale = 'en') {
  if (!supported(currency) || typeof amount !== 'string' || !/^\d+(?:\.\d+)?$/.test(amount)) return null;
  const [whole, fraction] = amount.split('.');
  try {
    const integer = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(BigInt(whole));
    if (fraction === undefined) return `${currency} ${integer}`;
    const suffix = new Intl.NumberFormat(locale, { minimumFractionDigits: fraction.length, maximumFractionDigits: fraction.length })
      .formatToParts(Number(`0.${fraction}`))
      .filter((part) => part.type === 'decimal' || part.type === 'fraction')
      .map((part) => part.value).join('');
    return `${currency} ${integer}${suffix}`;
  } catch {
    return `${currency} ${amount}`;
  }
}

export function interpolate(template, values) {
  return template.replace(/\{([A-Za-z]+)\}/g, (match, key) => own(values, key) ? String(values[key]) : match);
}

function translatedStrings(...sources) {
  const strings = { ...DEFAULT_STRINGS };
  for (const source of sources) {
    for (const key of Object.keys(DEFAULT_STRINGS)) {
      if (typeof source?.[key] === 'string' && source[key].trim() && source[key].length <= 1000) strings[key] = source[key];
    }
  }
  return strings;
}

export function normalizeConfig(input) {
  if (input?.schemaVersion !== 1 || input.baseCurrency !== 'SGD' || !/^\d{4}-\d{2}-\d{2}$/.test(input.fxDate ?? '')) throw new Error('Invalid reference configuration');
  const date = new Date(`${input.fxDate}T00:00:00Z`);
  if (Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== input.fxDate) throw new Error('Invalid reference date');
  const rates = { SGD: { numerator: '1', denominator: '1' } };
  for (const currency of Object.keys(CURRENCY_DECIMALS)) {
    const rate = input.rates?.[currency];
    if (currency !== 'SGD' && positiveDecimal(rate?.numerator) && positiveDecimal(rate?.denominator)) {
      rates[currency] = { numerator: rate.numerator, denominator: rate.denominator };
    }
  }
  const pricesByVariant = Object.create(null);
  for (const [variant, price] of Object.entries(input.pricesByVariant ?? {})) {
    if (/^\d+$/.test(variant) && positiveDecimal(price)) pricesByVariant[variant] = price;
  }
  const countryCurrency = { ...COUNTRY_CURRENCY };
  for (const [country, currency] of Object.entries(input.countryCurrency ?? {})) {
    if (/^[A-Z]{2}$/.test(country) && supported(currency)) countryCurrency[country] = currency;
  }
  return { schemaVersion: 1, baseCurrency: 'SGD', fxDate: input.fxDate, rates, pricesByVariant, countryCurrency, strings: translatedStrings(input.strings) };
}

export function detectedCountry(response) {
  const country = response?.detected_values?.country?.handle;
  return typeof country === 'string' && /^[A-Za-z]{2}$/.test(country) ? country.toUpperCase() : null;
}

export function currencyForCountry(country, countryCurrency = COUNTRY_CURRENCY) {
  const normalized = typeof country === 'string' ? country.toUpperCase() : '';
  return own(countryCurrency, normalized) && supported(countryCurrency[normalized]) ? countryCurrency[normalized] : 'SGD';
}

export function createSelectionState(preference) {
  return { manualCurrency: supported(preference) ? preference : null, country: null, detection: 'pending' };
}

export function reduceSelection(state, event) {
  if (event.type === 'choice' && (event.value === 'auto' || supported(event.value))) {
    return { ...state, manualCurrency: event.value === 'auto' ? null : event.value };
  }
  if (event.type === 'detected') {
    return { ...state, country: detectedCountry(event.response), detection: 'complete' };
  }
  if (event.type === 'failed') return { ...state, country: null, detection: 'complete' };
  return state;
}

export function resolveSelection(state, config) {
  if (!config) return { currency: 'SGD', mode: 'unavailable', country: '' };
  if (state.manualCurrency && own(config.rates, state.manualCurrency)) return { currency: state.manualCurrency, mode: 'manual', country: '' };
  if (state.detection === 'pending') return { currency: 'SGD', mode: 'detecting', country: '' };
  if (state.country && own(config.countryCurrency, state.country)) {
    const currency = currencyForCountry(state.country, config.countryCurrency);
    if (own(config.rates, currency)) return { currency, mode: 'detected', country: state.country };
  }
  return { currency: 'SGD', mode: 'fallback', country: '' };
}

export function referenceText(variantId, selection, config, locale = 'en', strings = config?.strings) {
  const price = config?.pricesByVariant?.[variantId];
  const source = convertPrice(price, 'SGD', config?.rates);
  const amount = convertPrice(price, selection.currency, config?.rates);
  if (source === null || amount === null) return null;
  return interpolate(strings[selection.currency === 'SGD' ? 'sourcePrice' : 'estimate'], {
    source: formatAmount(source, 'SGD', locale), amount: formatAmount(amount, selection.currency, locale), currency: selection.currency
  });
}

export function browsingContextURL(root, base) {
  const origin = new URL(base).origin;
  const route = typeof root === 'string' && root.endsWith('/') ? root : '/';
  const url = new URL(`${route}browsing_context_suggestions.json?country[enabled]=true`, base);
  if (url.origin !== origin || url.username || url.password) throw new Error('Browsing context must stay on the storefront');
  return url.href;
}

export function configAssetURL(value, base) {
  const url = new URL(value, base);
  const sameOrigin = url.origin === new URL(base).origin;
  if ((!sameOrigin && !(url.protocol === 'https:' && url.hostname === 'cdn.shopify.com')) || url.username || url.password || !/\/assets\/[^/]+\.json$/.test(url.pathname)) {
    throw new Error('Reference configuration must be a Shopify theme asset');
  }
  return url.href;
}

export async function fetchJSON(url, { fetch: fetcher, signal, timeout = 3000, credentials = 'same-origin', mode = 'same-origin', setTimeout: startTimer = globalThis.setTimeout, clearTimeout: stopTimer = globalThis.clearTimeout } = {}) {
  const controller = new AbortController();
  let timer;
  let cancel;
  const stopped = new Promise((resolve, reject) => {
    cancel = () => { controller.abort(); reject(new Error('Reference request aborted')); };
    if (signal?.aborted) cancel();
    else signal?.addEventListener('abort', cancel, { once: true });
    timer = startTimer(() => { controller.abort(); reject(new Error('Reference request timed out')); }, timeout);
  });
  try {
    return await Promise.race([
      stopped,
      (async () => {
        const response = await fetcher(url, { method: 'GET', credentials, mode, redirect: 'error', signal: controller.signal, headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Reference request failed');
        return response.json();
      })()
    ]);
  } finally {
    stopTimer(timer);
    signal?.removeEventListener('abort', cancel);
  }
}

function setText(node, text) {
  if (node && node.textContent !== text) node.textContent = text;
}

function inlineStrings(element) {
  try { return JSON.parse(element.querySelector('script[type="application/json"][data-currency-strings]')?.textContent || '{}'); }
  catch { return {}; }
}

export function mountCurrencyReference(element, browser) {
  const document = element.ownerDocument;
  const abort = new AbortController();
  let preference;
  try { preference = browser.localStorage.getItem(STORAGE_KEY); } catch {}
  let state = createSelectionState(preference);
  let config = null;
  let disposed = false;
  let scheduled = false;
  let detectionStarted = false;
  let strings = translatedStrings(inlineStrings(element));
  const checkout = /^[A-Z]{3}$/.test(element.dataset.checkoutCurrency ?? '') ? element.dataset.checkoutCurrency : 'PHP';
  const locale = element.dataset.locale || document.documentElement.lang || 'en';
  const queryAll = (selector) => document.querySelectorAll(selector);
  const selection = () => resolveSelection(state, config);

  function render() {
    if (disposed) return;
    const selected = selection();
    const values = { currency: selected.currency, country: selected.country, checkoutCurrency: checkout, date: config?.fxDate || '' };
    for (const control of element.querySelectorAll(CHOICE_SELECTOR)) {
      for (const option of control.options) option.disabled = option.value !== 'auto' && (!supported(option.value) || (config && !own(config.rates, option.value)));
      control.disabled = !config;
      const value = state.manualCurrency && config && own(config.rates, state.manualCurrency) ? state.manualCurrency : 'auto';
      if (control.value !== value) control.value = value;
    }
    const statusKey = { unavailable: 'statusUnavailable', detecting: 'statusDetecting', detected: 'statusDetected', manual: 'statusManual', fallback: 'statusFallback' }[selected.mode];
    setText(element.querySelector('[data-currency-status]'), interpolate(strings[statusKey], values));
    const rateDate = element.querySelector('[data-rate-date]');
    setText(rateDate, config ? interpolate(strings.rateDate, values) : '');
    if (rateDate) rateDate.hidden = !config;
    for (const target of queryAll(PRICE_SELECTOR)) {
      const text = referenceText(target.dataset.variantId, selected, config, locale, strings);
      setText(target, text || '');
      target.hidden = text === null;
    }
    for (const note of queryAll(NOTE_SELECTOR)) {
      setText(note, config ? interpolate(strings.note, values) : '');
      note.hidden = !config;
    }
  }

  async function detect() {
    if (detectionStarted || disposed) return;
    detectionStarted = true;
    try {
      const url = browsingContextURL(browser.Shopify?.routes?.root, browser.location.href);
      const response = await fetchJSON(url, { fetch: browser.fetch.bind(browser), signal: abort.signal });
      if (!disposed) state = reduceSelection(state, { type: 'detected', response });
    } catch {
      if (!disposed) state = reduceSelection(state, { type: 'failed' });
    }
    render();
  }

  function change(event) {
    const target = event.target;
    if (!target?.matches?.(CHOICE_SELECTOR) || !element.contains(target)) return;
    const value = target.value;
    if (value !== 'auto' && (!supported(value) || !config || !own(config.rates, value))) { render(); return; }
    state = reduceSelection(state, { type: 'choice', value });
    try {
      if (value === 'auto') browser.localStorage.removeItem(STORAGE_KEY);
      else browser.localStorage.setItem(STORAGE_KEY, value);
    } catch {}
    render();
    if (value === 'auto') void detect();
  }

  const observer = new browser.MutationObserver((records) => {
    const relevant = records.some((record) => record.type === 'attributes' || [...record.addedNodes].some((node) =>
      node.nodeType === 1 && (node.matches?.(`${PRICE_SELECTOR}, ${NOTE_SELECTOR}, ${CHOICE_SELECTOR}`) || node.querySelector?.(`${PRICE_SELECTOR}, ${NOTE_SELECTOR}, ${CHOICE_SELECTOR}`))));
    if (relevant && !scheduled && !disposed) {
      scheduled = true;
      queueMicrotask(() => { scheduled = false; render(); });
    }
  });
  observer.observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-variant-id'] });
  element.addEventListener('change', change);
  render();
  const ready = (async () => {
    try {
      const url = configAssetURL(element.dataset.configUrl, browser.location.href);
      const raw = await fetchJSON(url, { fetch: browser.fetch.bind(browser), signal: abort.signal, mode: 'cors' });
      if (disposed) return;
      config = normalizeConfig(raw);
      strings = translatedStrings(config.strings, inlineStrings(element));
      render();
      if (!state.manualCurrency || !own(config.rates, state.manualCurrency)) await detect();
    } catch {
      if (!disposed) { config = null; render(); }
    }
  })();

  return {
    ready,
    selection,
    dispose() {
      if (disposed) return;
      disposed = true;
      abort.abort();
      observer.disconnect();
      element.removeEventListener('change', change);
    }
  };
}

export function registerCurrencyReference(browser) {
  if (!browser?.customElements || browser.customElements.get('aurelia-currency-reference')) return;
  browser.customElements.define('aurelia-currency-reference', class extends browser.HTMLElement {
    connectedCallback() {
      if (!this.currencyReference) this.currencyReference = mountCurrencyReference(this, browser);
    }
    disconnectedCallback() {
      this.currencyReference?.dispose();
      this.currencyReference = null;
    }
  });
}

if (typeof window !== 'undefined') registerCurrencyReference(window);
